import React, { useState } from 'react';
import classnames from 'classnames';
import { makeStyles } from '@material-ui/core/styles';

import Translate from '@hydra/common/components/i18n';
import { useReviewContext } from '@hydra/common/context/review';

const isOdd = (num: number) => num % 2;

const useStyles = makeStyles(theme => ({
  table: {
    border: '0',
    width: '100%',
    tableLayout: 'fixed',
    maxWidth: '100%',
    backgroundColor: 'rgba(0,0,0,0)',
    borderCollapse: 'collapse',
    borderSpacing: 0
  },
  thead: {
    /* fixes chrome print issue with tableheaders
    * see: https://stackoverflow.com/questions/39516885/workaround-for-chrome-53-printing-table-header-twice-on-2nd-and-later-pages */
    display: 'table-row-group'
  },
  th: {
    borderTop: 0,
    borderBottom: `2px solid ${theme.palette.secondary.light}`,
    padding: theme.spacing(1),
    lineHeight: '1.6'
  },
  td: {
    wordBreak: 'break-word',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    padding: theme.spacing(1),
    lineHeight: '1.6',
    verticalAlign: 'top',
    borderTop: `1px solid ${theme.palette.secondary.light}`
  },
  borderLeft: {
    textAlign: 'right',
    borderLeft: `1px solid ${theme.palette.secondary.light}`
  },
  alignRight: {
    textAlign: 'right'
  },
  alignLeft: {
    textAlign: 'left'
  },
  center: {
    textAlign: 'center'
  },
  verticalAlignMiddle: {
    verticalAlign: 'middle'
  },
  nowrap: {
    whiteSpace: 'nowrap'
  },
  thinHead: {
    borderBottomWidth: '1px'
  },
  striped: {
    backgroundColor: theme.palette.secondary.smoke
  },
  showMore: {
    color: theme.palette.primary.darker,
    borderTop: '1px solid #ddd',
    '&:hover': {
      textDecoration: 'underline',
      backgroundColor: theme.palette.secondary.smoke
    }
  }
}));

export function getColumnStyles ({ head, fixedColumns }: { head: JSX.Element[], fixedColumns?: number[] }) {
  if (!fixedColumns) {
    return head.map(() => ({}));
  }

  const total = fixedColumns.reduce((sum, column) => sum + column, 0);
  return fixedColumns.map(column => {
    const percentage = Math.floor(100 * column / total);
    return ({ width: `${percentage}%` });
  });
}

export function TableHead ({
  head,
  fixedColumns,
  thinHead,
  classNames = [],
  alignLeft
}: {
  head: JSX.Element[]
  fixedColumns?: number[]
  thinHead?: boolean
  classNames?: string[]
  alignLeft: boolean
}) {
  const classes = useStyles();
  if (head.length === 0) {
    return null;
  }

  const headerStyles = getColumnStyles({ head, fixedColumns });
  return (
    <thead className={classes.thead}>
      <tr>
        {
          head.map((headItem, index) => {
            const className = classnames(
              classes.th,
              classes[classNames[index] as ClassNamesTable],
              thinHead && classes.thinHead,
              alignLeft && classes.alignLeft
            );
            return (
              <th
                key={index}
                className={className}
                style={headerStyles[index]}
              >
                {headItem}
              </th>
            );
          })
        }
      </tr>
    </thead>
  );
}

export function TableBody ({
  visibleRows,
  striped,
  nowrap,
  classNames = []
}: {
  visibleRows: JSX.Element[][]
  striped: boolean
  nowrap?: boolean
  classNames?: string[]
}) {
  const classes = useStyles();
  return (
    <tbody>
      {
        visibleRows.map((row, rowIndex) => Array.isArray(row)
          ? (
            <tr key={rowIndex} className={classnames(striped && isOdd(rowIndex + 1) ? classes.striped : null)}>
              {row.map((column, colIndex) => (
                <td
                  key={colIndex}
                  className={classnames(classes[classNames[colIndex] as ClassNamesTable], classes.td, nowrap ? classes.nowrap : null)}
                >
                  {column}
                </td>
              ))}
            </tr>
            )
          : React.cloneElement(row, { key: rowIndex })
        )
      }
    </tbody>
  );
}

function useVisibleData ({ rows, showAmount }: { rows: JSX.Element[][], showAmount: number }) {
  const [showAll, setShowAll] = useState(false);
  const classes = useStyles();
  const { isPdf } = useReviewContext();

  const handleClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation();
    return setShowAll(s => !s);
  };

  const sum = rows.length;
  const showPartial = (!isNaN(showAmount) && sum > showAmount);
  const visibleRows = showPartial && !showAll ? rows.slice(0, showAmount) : rows;

  const extraContent = showPartial
    ? isPdf
        ? <Translate content='pdf_and_more' with={{ count: sum - showAmount }} />
        : (
          <div className={classnames(classes.center, classes.showMore)} onClick={handleClick}>
            <Translate content={showAll ? 'view_less_data' : 'show_more'} component='div' />
          </div>
          )
    : null;

  return {
    visibleRows,
    extraContent
  };
}

type ClassNamesTable = 'center' | 'verticalAlignMiddle' | 'nowrap' | 'alignRight' | 'borderLeft';

export function CriteriumTable ({
  showAmount = 5,
  fixedColumns,
  head = [],
  rows,
  className,
  thinHead,
  striped,
  nowrap,
  classNames = {}
}: {
  showAmount: number
  fixedColumns?: number[]
  head: JSX.Element[]
  rows: JSX.Element[][]
  className?: string
  thinHead?: boolean
  striped: boolean
  nowrap?: boolean
  classNames?: { head?: string[], body?: string[] }
}) {
  const { visibleRows, extraContent } = useVisibleData({ rows, showAmount });
  const classes = useStyles();
  const tableClasses = classnames(className, classes.table);

  return (
    <>
      <table className={tableClasses}>
        <TableHead
          head={head}
          fixedColumns={fixedColumns}
          thinHead={thinHead}
          classNames={classNames && classNames.head}
          alignLeft
        />
        <TableBody
          visibleRows={visibleRows}
          striped={striped}
          nowrap={nowrap}
          classNames={classNames && classNames.body}
        />
      </table>
      {extraContent}
    </>
  );
}

export default CriteriumTable;
